import {
  Component,
  OnInit,
  Renderer2,
  ChangeDetectorRef,
  AfterViewChecked,
  AfterViewInit,
  afterEveryRender,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { Globals } from './globals';
import { AppState } from './types/app-state';
import { MetaService } from './services/meta.service';
import { LoaderService } from './services/loader.service';
import { SessionService } from './services/session.service';
import { SearchService } from 'app/services/search.service';
import { ConfigService } from './services/config.service';
import { AccountService } from './services/account.service';
import { LocalStorageService } from './services/localStorage.service';
import { environment } from 'environments/environment';
import { SwUpdate } from '@angular/service-worker';
import { ThemeIconService } from './services/theme-icon.service';
import { PendoService } from './services/pendo.service';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let hj: any;
declare let pendo: any;
declare function pendoIntialize(apiKey): void;
declare let gtag: any;
declare let transcend;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewChecked, AfterViewInit {
  toggles$: Observable<any[]>;
  activeToggleClasses: string[] = [];
  message: string;
  subscription: Subscription = new Subscription();
  // Switch to false for scully
  loadingContent = environment.envName === 'development' ? false : true;
  redererHook;
  imageUpdateHook;
  internalDomains = this.globals.internalDomains;

  constructor(
    private store: Store<AppState>,
    protected renderer: Renderer2,
    public globals: Globals,
    public configService: ConfigService,
    public loaderService: LoaderService,
    private changeRef: ChangeDetectorRef,
    private sessionService: SessionService,
    private searchService: SearchService,
    private accountService: AccountService,
    private localStorageService: LocalStorageService,
    private metaService: MetaService,
    private router: Router,
    private swUpdate: SwUpdate,
    private themeService: ThemeIconService,
    private pendoService: PendoService
  ) {
    this.toggles$ = this.store.pipe(select('toggle'));
    this.imageUpdateHook = afterEveryRender(() => {
      if (
        this.router.url.includes('/apis/payrix/test') ||
        this.router.url.includes('/') ||
        this.router.url.includes('/apis/payrix/test-v2')
      ) {
        const currentTheme = this.themeService.getDefaultSetting();
        const masthead = document.querySelector('masthead-secondary') || document.querySelector('secondary-masthead');
        if (masthead) {
          const img = masthead.querySelector('img');
          if (img) {
            let imgSrc = img.getAttribute('src');
            if (currentTheme === 'light') {
              imgSrc = imgSrc.replace('dark', currentTheme);
            }

            if (currentTheme === 'dark') {
              imgSrc = imgSrc.replace('light', currentTheme);
            }
            img.setAttribute('src', imgSrc);
          }
          this.imageUpdateHook.destroy();
        }
      } else {
        this.imageUpdateHook.destroy();
      }
    });
  }

  ngOnInit() {
    // const documentThemeEle = document.documentElement;
    // setTimeout(() => {
    //   documentThemeEle.setAttribute('data-theme', 'dark');
    // }, 5000);
    // console.log(documentEle);

    // if (environment.googleAnalytics) {
    //   const googleAnalyticsScript = this.renderer2.createElement('script');
    //   googleAnalyticsScript.id = 'google-analytics';
    //   googleAnalyticsScript.defer = true;
    //   googleAnalyticsScript.type = 'text/javascript';
    //   googleAnalyticsScript.nonce = '56gibS69Ljrqq6';
    //   if (environment.envName === 'prod') {
    //     googleAnalyticsScript.src = '../assets/google-analytics/18344670-2';
    //   } else {
    //     googleAnalyticsScript.src = '../assets/google-analytics/18344670-1';
    //   }
    // }

    if (this.swUpdate.isEnabled) {
      console.log(`Version management enabled:`);
      this.swUpdate.versionUpdates.subscribe((event) => {
        console.log(`recieved a event`);
        switch (event.type) {
          case 'VERSION_DETECTED':
            console.log(event);
            console.log(`Downloading new app version: ${event.version.hash}`);
            break;
          case 'VERSION_READY':
            console.log(event);
            console.log(`Current app version: ${event.currentVersion.hash}`);
            console.log(`New app version ready for use: ${event.latestVersion.hash}`);
            window.location.reload();
            break;
          case 'VERSION_INSTALLATION_FAILED':
            console.log(event);
            console.log(`Failed to install app version '${event.version.hash}': ${event.error}`);
            break;
        }
      });
    } else {
      console.log(`Version management not enabled:`);
    }

    if (environment.googleAnalytics) {
      const script: HTMLScriptElement = document.createElement('script');
      script.id = 'ganalytics';
      script.setAttribute('type', 'text/javascript');
      script.src = environment.googleAnalyticsUrl + environment.googleAnalytics;
      script.async = true;
      document.head.appendChild(script);
      this.headListener();
    }

    if (environment.pendoApiKey) {
      this.pendoService.installPendo();
    }

    this.addMarketoTrackingCode();

    // Cognito Session
    this.sessionService.createSession();

    // Loader subscription
    this.subscription.add(
      this.loaderService.loadingContent.subscribe((loading: boolean) => {
        // console.trace(loading);
        this.loadingContent = loading;
      })
    );

    this.subscription.add(
      this.searchService.showSearch.subscribe((bool) => {
        if (bool) {
          this.renderer.addClass(document.documentElement, 'is-scroll-disabled');
        } else {
          this.renderer.removeClass(document.documentElement, 'is-scroll-disabled');
        }
      })
    );
  }

  private getPendoVisitorContext(): { visitorId?: string; isInternalUser: boolean } {
    let visitorId;
    let isInternalUser = false;
    if (this.sessionService.getUserInfo()) {
      const userJwt = this.sessionService.getJWT();
      const userJwtParts = userJwt ? userJwt.split('.') : [];
      if (userJwtParts && userJwtParts.length > 1) {
        const userObj = JSON.parse(atob(userJwtParts[1]));
        visitorId = userObj.username;
      }
    }
    const accountInfo = this.accountService.accountInfo.getValue();
    if (accountInfo && accountInfo.email) {
      isInternalUser = this.internalDomains.some((domain) => accountInfo.email.endsWith(domain));
    }
    return { visitorId, isInternalUser };
  }

  addMarketoTrackingCode() {
    const script: HTMLScriptElement = this.renderer.createElement('script');
    script.id = 'marketo';
    script.setAttribute('type', 'text/javascript');
    script.text = `!function(){var e=!1;function t(){!1===e&&(e=!0,Munchkin.init("${environment.marketoCodeId}"))}var a=document.createElement("script");a.type="text/javascript",a.async=!0,a.src="${environment.marketoCodeSrcUrl}",a.onreadystatechange=function(){("complete"==this.readyState||"loaded"==this.readyState)&&t()},a.onload=t,document.getElementsByTagName("head")[0].appendChild(a)}();`;
    this.renderer.appendChild(document.body, script);
  }

  headListener = () => {
    const headElement = document.querySelector('head');
    const observer = new MutationObserver((mutatedEntries) => {
      mutatedEntries.forEach((mutation) => {
        const addedNodes = mutation.addedNodes;
        if (addedNodes.length > 0) {
          const addedElement: any = mutation.addedNodes[0];
          if (addedElement.id === 'ganalytics') {
            observer.disconnect();
            const nextScript: HTMLScriptElement = document.createElement('script');
            nextScript.id = 'gainitialize';
            nextScript.text = `window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('js', new Date());gtag('config', '${environment.googleAnalytics}');`;
            document.head.appendChild(nextScript);

            if (typeof gtag !== 'undefined') {
              this.router.events.subscribe((event) => {
                if (event instanceof NavigationEnd) {
                  gtag('config', environment.googleAnalytics, { page_path: event.urlAfterRedirects });
                }
              });
            }
          }
        }
      });
    });
    observer.observe(headElement, { childList: true });
  };

  ngAfterViewChecked() {
    this.changeRef.detectChanges();
  }

  ngAfterViewInit(): void {
    if (!window.location.href.includes('/login')) {
      const previousPageUrl = this.localStorageService.getItem('previousPageUrl');
      const confirmRedirection = this.localStorageService.getItem('confirmRedirection');
      this.localStorageService.removeItem('previousPageUrl');
      this.localStorageService.removeItem('confirmRedirection');
      const isSafeRelative = (url: string) => {
        if (!url) return false;
        return /^\/(?!\/).*$/g.test(url.trim());
      };
      if (previousPageUrl && confirmRedirection && isSafeRelative(previousPageUrl)) {
        this.router.navigateByUrl(previousPageUrl);
      }
    }
  }
}
